import React, { useMemo, useState } from "react";
import { VoiceCandidate, VoiceCriteria } from "../core/types";
import VoicePanelShell from "./VoicePanelShell";

const SLOT_LABEL: Partial<Record<keyof VoiceCriteria, string>> = {
  source: "Departure",
  destination: "Arrival",
};

interface VoiceAmbiguityChooserProps {
  candidates: VoiceCandidate[];
  transcript: string;
  onResolve: (selections: Partial<Record<keyof VoiceCriteria, string>>) => void;
  onCancel: () => void;
}

/**
 * Step 4: the server's question. AMBIGUOUS_AIRPORT means it refuses to guess
 * ("flights to london" is genuinely 5 airports) and sends candidates instead.
 * Grouped by slot, because more than one slot can be ambiguous in the same
 * response. Picking is client-side only — no second API call.
 *
 * With a single slot left to answer, the pick itself is the answer and the
 * chooser submits straight away; with several, each pick is held until all
 * questions are answered.
 */
const VoiceAmbiguityChooser: React.FC<VoiceAmbiguityChooserProps> = ({
  candidates,
  transcript,
  onResolve,
  onCancel,
}) => {
  const groups = useMemo(() => {
    const bySlot = new Map<keyof VoiceCriteria, VoiceCandidate[]>();
    for (const candidate of candidates) {
      const list = bySlot.get(candidate.slot) ?? [];
      list.push(candidate);
      bySlot.set(candidate.slot, list);
    }
    return Array.from(bySlot.entries());
  }, [candidates]);

  const [selections, setSelections] = useState<Partial<Record<keyof VoiceCriteria, string>>>({});

  const select = (slot: keyof VoiceCriteria, code: string) => {
    const next = { ...selections, [slot]: code };
    setSelections(next);
    if (groups.every(([groupSlot]) => next[groupSlot])) onResolve(next);
  };

  return (
    <VoicePanelShell onClose={onCancel} closeLabel="Cancel voice search">
      <div className="flex flex-col items-center justify-center">
        {/* The words that raised the question lead the screen — the chips
            below only make sense against what was heard. */}
        <p className="max-w-[900px] text-center font-inter text-base font-semibold text-white sm:text-[17px]">
          &ldquo; {transcript} &rdquo;
        </p>

        {groups.map(([slot, options]) => (
          <div key={slot} className="mt-2 flex w-full flex-col items-center">
            <p className="font-inter text-[13px] text-white/55">
              Which {SLOT_LABEL[slot] ?? slot} airport?
            </p>

            <div className="mt-3 flex flex-wrap items-center justify-center gap-2.5">
              {options.map((option) => {
                const selected = selections[slot] === option.code;
                return (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => select(slot, option.code)}
                    className={`rounded-full border px-4 py-1.5 font-inter transition-colors cursor-pointer ${
                      selected
                        ? "border-[#FFBC00] bg-[#FFBC00] text-[#192227]"
                        : "border-[#FFBC00]/25 bg-white/[0.04] hover:bg-white/[0.09]"
                    }`}
                  >
                    <span
                      className={`text-base font-bold ${selected ? "text-[#192227]" : "text-[#FFBC00]"}`}
                    >
                      {option.code}
                    </span>
                    <span
                      className={`ml-1.5 text-xs ${selected ? "text-[#192227]/80" : "text-white/70"}`}
                    >
                      ({option.name})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

      </div>
    </VoicePanelShell>
  );
};

export default VoiceAmbiguityChooser;
